import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, ShieldAlert, ShieldCheck, FileWarning, Gauge,
  ScrollText, Landmark, Send, History, AlertTriangle, UserCheck,
} from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { mxn, fdate } from "@/lib/format";

const SEM = {
  rojo: "border-rose-300 bg-rose-50 text-rose-800",
  ambar: "border-amber-300 bg-amber-50 text-amber-900",
  verde: "border-emerald-300 bg-emerald-50 text-emerald-800",
};
const SEM_LABEL = { rojo: "Rojo", ambar: "Ámbar", verde: "Verde" };

export const Semaforo = ({ nivel, children, testid }) => (
  <span data-testid={testid}
    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${SEM[nivel] || SEM.verde}`}>
    <span className={`h-2 w-2 rounded-full ${nivel === "rojo" ? "bg-rose-500" : nivel === "ambar" ? "bg-amber-500" : "bg-emerald-500"}`} />
    {children || SEM_LABEL[nivel] || nivel || "—"}
  </span>
);

export const Bloque = ({ n, titulo, icono: Icono, subtitulo, testid, children, extra }) => (
  <section data-testid={testid} className="rounded-lg border border-slate-200 bg-white">
    <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3">
      <div className="flex items-center gap-2.5">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">{n}</span>
        {Icono && <Icono className="h-4 w-4 text-slate-400" />}
        <div>
          <h2 className="text-base font-semibold text-slate-900">{titulo}</h2>
          {subtitulo && <p className="text-xs text-slate-500">{subtitulo}</p>}
        </div>
      </div>
      {extra}
    </header>
    <div className="px-5 py-4">{children}</div>
  </section>
);

const Campo = ({ label, valor }) => (
  <div className="min-w-0">
    <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
    <p className="truncate text-sm text-slate-800">{valor || "—"}</p>
  </div>
);

const Vacio = ({ children, testid }) => (
  <p data-testid={testid} className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-center text-sm text-slate-500">
    {children}
  </p>
);

const Tabla = ({ cols, rows, render, testid, vacio }) =>
  !rows?.length ? <Vacio testid={`${testid}-vacio`}>{vacio}</Vacio> : (
    <div className="overflow-x-auto">
      <table data-testid={testid} className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-400">
            {cols.map((c) => <th key={c} className="whitespace-nowrap px-2 py-2 font-semibold">{c}</th>)}
          </tr>
        </thead>
        <tbody>{rows.map(render)}</tbody>
      </table>
    </div>
  );

export default function AvFichaCliente() {
  const { clienteId } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let vivo = true;
    (async () => {
      setCargando(true); setError("");
      try {
        const { data } = await api.get(`/av/clientes/${clienteId}/ficha`);
        if (vivo) setD(data);
      } catch (e) {
        if (vivo) setError(e?.response?.data?.detail || "No fue posible cargar la ficha del cliente.");
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => { vivo = false; };
  }, [clienteId]);

  if (cargando) return (
    <div data-testid="av-ficha-cargando" className="flex items-center justify-center gap-2 py-24 text-slate-500">
      <Loader2 className="h-5 w-5 animate-spin" /> Cargando el expediente del cliente…
    </div>
  );

  if (error) return (
    <div className="p-6 md:p-8">
      <div data-testid="av-ficha-error" className="mx-auto max-w-xl rounded-lg border border-amber-300 bg-amber-50 p-6 text-center">
        <ShieldAlert className="mx-auto h-8 w-8 text-amber-600" />
        <p className="mt-3 text-sm text-amber-900">{error}</p>
        <Button data-testid="av-ficha-volver-error" variant="outline" className="mt-4" onClick={() => navigate("/r/clientes_av")}>
          <ArrowLeft className="mr-1.5 h-4 w-4" /> Volver a Clientes
        </Button>
      </div>
    </div>
  );

  const h = d.encabezado;
  const ebrRojo = ["alto", "rojo"].includes(String(h.semaforo_ebr || h.grado_ebr).toLowerCase());

  return (
    <div data-testid="av-ficha-cliente" className="pb-16">
      <PageHeader
        title="Expediente del cliente"
        subtitle={`${d.actividad} · ${d.fundamento}`}>
        <Button data-testid="av-ficha-volver" variant="outline" size="sm" onClick={() => navigate("/r/clientes_av")}>
          <ArrowLeft className="mr-1.5 h-4 w-4" /> Clientes
        </Button>
      </PageHeader>

      <div className="space-y-5 p-6 md:p-8">
        {/* Encabezado: las dos preguntas del Oficial de Cumplimiento, de entrada. */}
        <div data-testid="av-ficha-encabezado" className="rounded-lg border border-slate-200 bg-white p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 data-testid="av-ficha-nombre" className="font-heading text-xl font-semibold text-slate-900">{h.nombre}</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                <span data-testid="av-ficha-rfc">{h.rfc || "sin RFC"}</span>
                {" · "}{h.tipo_persona}{" · "}{h.estatus_cliente}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span data-testid="av-ficha-grado-ebr"
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${ebrRojo ? SEM.rojo : SEM.verde}`}>
                <Gauge className="h-3.5 w-3.5" /> Riesgo EBR: {h.grado_ebr || "sin calificar"}
              </span>
              <Semaforo testid="av-ficha-semaforo-acumulado" nivel={h.semaforo_acumulado}>
                Umbral 6 meses: {SEM_LABEL[h.semaforo_acumulado] || "—"} · {h.porcentaje_acumulado ?? 0}%
                {h.fraccion_peor ? ` (Fr. ${h.fraccion_peor})` : ""}
              </Semaforo>
              <Badge data-testid="av-ficha-badge-expediente"
                variant={h.expediente_estatus === "Completo" ? "default" : "destructive"}>
                Expediente {h.expediente_estatus}
              </Badge>
              <Badge data-testid="av-ficha-badge-listas"
                variant={h.coincidencias_listas ? "destructive" : "secondary"}>
                {h.coincidencias_listas ? `${h.coincidencias_listas} coincidencia(s) en listas` : "Sin coincidencias"}
              </Badge>
            </div>
          </div>
        </div>

        {/* 1 · Identificación */}
        <Bloque n={1} titulo="Identificación del cliente" icono={ScrollText}
          subtitulo="LFPIORPI Art. 18" testid="av-bloque-identificacion"
          extra={d.identificacion.campos_faltantes_art_18?.length ? (
            <Badge data-testid="av-id-faltantes" variant="destructive">
              Faltan {d.identificacion.campos_faltantes_art_18.length}: {d.identificacion.campos_faltantes_art_18.join(", ")}
            </Badge>
          ) : <Badge variant="secondary">Datos del Art. 18 completos</Badge>}>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <Campo label="Nombre / Razón social" valor={d.identificacion.nombre_completo} />
            <Campo label="RFC" valor={d.identificacion.rfc} />
            <Campo label="CURP" valor={d.identificacion.curp} />
            <Campo label="Tipo de persona" valor={d.identificacion.tipo_persona} />
            <Campo label="Fecha de nacimiento" valor={fdate(d.identificacion.fecha_nacimiento)} />
            <Campo label="Género" valor={d.identificacion.genero} />
            <Campo label="Nacionalidad" valor={d.identificacion.nacionalidad} />
            <Campo label="País de residencia" valor={d.identificacion.pais_residencia} />
            <Campo label="Ocupación / actividad" valor={d.identificacion.ocupacion} />
            <Campo label="Identificación" valor={`${d.identificacion.identificacion || ""} ${d.identificacion.numero_identificacion || ""}`.trim()} />
            <Campo label="Domicilio" valor={d.identificacion.domicilio || d.identificacion.calle_y_numero} />
            <Campo label="Entidad federativa" valor={d.identificacion.entidad_federativa} />
            <Campo label="Teléfono" valor={d.identificacion.telefono} />
            <Campo label="Correo" valor={d.identificacion.correo} />
            <Campo label="Alta del cliente" valor={fdate(d.identificacion.created_at)} />
            <Campo label="Canal de contratación" valor={d.identificacion.canal_contratacion} />
          </div>
        </Bloque>

        {/* 2 · Screening */}
        <Bloque n={2} titulo="Cotejo contra listas oficiales" icono={ShieldCheck}
          subtitulo={d.screening.corrida
            ? `Última corrida ${fdate(d.screening.corrida.fecha)} · ${d.screening.corrida.listas_cruzadas?.length || 0} listas cruzadas · folio ${(d.screening.corrida.run_id || "").slice(0, 8)}`
            : "Sin corridas registradas"}
          testid="av-bloque-screening"
          extra={<div className="flex gap-2">
            {d.screening.pep && <Badge variant="destructive">PEP</Badge>}
            {d.screening.pendiente && <Badge variant="destructive">Cotejo pendiente</Badge>}
            <Badge variant={d.screening.total_coincidencias ? "destructive" : "secondary"}>
              {d.screening.total_coincidencias} coincidencia(s)
            </Badge>
          </div>}>
          <Tabla testid="av-tabla-screening" vacio="Sin coincidencias en listas oficiales."
            cols={["Lista", "Versión", "Score", "Tipo", "Fecha", "Estatus", "Sello"]}
            rows={d.screening.coincidencias}
            render={(c) => (
              <tr key={c.match_id} data-testid={`av-match-${c.match_id}`} className="border-b border-slate-100">
                <td className="px-2 py-2 font-medium text-slate-800">
                  {c.lista} {c.critica && <Badge variant="destructive" className="ml-1">crítica</Badge>}
                </td>
                <td data-testid={`av-match-version-${c.match_id}`} className="whitespace-nowrap px-2 py-2 text-slate-600">{c.lista_version || "—"}</td>
                <td data-testid={`av-match-score-${c.match_id}`} className="px-2 py-2 font-semibold">{c.score}</td>
                <td className="px-2 py-2 text-slate-600">{c.tipo_coincidencia} · {c.campo_coincidente}</td>
                <td data-testid={`av-match-fecha-${c.match_id}`} className="whitespace-nowrap px-2 py-2 text-slate-600">{fdate(c.fecha)}</td>
                <td className="px-2 py-2"><Badge variant={c.estatus === "descartada" ? "secondary" : "destructive"}>{c.estatus}</Badge></td>
                <td className="px-2 py-2 text-xs text-slate-400">{c.sello || "—"}</td>
              </tr>
            )} />
        </Bloque>

        {/* 3 · EBR */}
        <Bloque n={3} titulo="Grado de riesgo (EBR)" icono={Gauge}
          subtitulo={d.ebr.hay_cedula
            ? `Cédula ${fdate(d.ebr.cedula?.fecha)} · ${d.ebr.cedula?.tipo} · metodología v${d.ebr.metodologia_version} (guardada en el ${d.ebr.cedula?.guardada_en})`
            : "El cliente aún no tiene cédula de riesgo"}
          testid="av-bloque-ebr"
          extra={<div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Puntaje {d.ebr.puntaje_total ?? "—"}</span>
            <Semaforo nivel={d.ebr.semaforo} testid="av-ebr-semaforo">{d.ebr.grado || "sin calificar"}</Semaforo>
          </div>}>
          {d.ebr.motivo && <p className="mb-3 text-sm text-slate-600">{d.ebr.motivo}</p>}
          <Tabla testid="av-tabla-ebr" vacio="No hay metodología EBR vigente con factores."
            cols={["Factor", "Ponderación", "Puntaje", "Subvariables evaluadas"]}
            rows={d.ebr.factores}
            render={(f) => (
              <tr key={f.clave} data-testid={`av-ebr-factor-${f.clave}`} className="border-b border-slate-100">
                <td className="px-2 py-2 font-medium text-slate-800">{f.nombre || f.clave}</td>
                <td className="px-2 py-2 text-slate-600">{f.ponderacion ?? "—"}%</td>
                <td className="px-2 py-2 font-semibold">{f.score ?? "—"}</td>
                <td className="px-2 py-2 text-xs text-slate-500">
                  {Object.entries(f.respuestas || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")
                    || (f.subvariables || []).map((s) => s.nombre).join(" · ") || "—"}
                </td>
              </tr>
            )} />
          {!!d.ebr.factores_que_elevan?.length && (
            <p data-testid="av-ebr-eleva" className="mt-3 text-sm text-rose-700">
              Factores que elevan el riesgo: {d.ebr.factores_que_elevan.map((x) => x.nombre || x).join(", ")}
            </p>
          )}
        </Bloque>

        {/* 4 · Expediente */}
        <Bloque n={4} titulo="Expediente de identificación" icono={FileWarning}
          subtitulo={`${d.expediente.completos} de ${d.expediente.total} documentos obligatorios · ${d.expediente.total_documentos_cargados} archivo(s) cargado(s)`}
          testid="av-bloque-expediente"
          extra={<Badge data-testid="av-expediente-estatus"
            variant={d.expediente.completo ? "default" : "destructive"}>{d.expediente.estatus}</Badge>}>
          <Tabla testid="av-tabla-expediente" vacio="No falta ningún documento obligatorio."
            cols={["Documento faltante", "Obligatorio", "Detalle"]}
            rows={d.expediente.faltantes}
            render={(i, ix) => (
              <tr key={i.canonical || ix} data-testid={`av-doc-falta-${i.canonical || ix}`} className="border-b border-slate-100">
                <td className="px-2 py-2 font-medium text-slate-800">{i.tipo_documento || i.canonical}</td>
                <td className="px-2 py-2"><Badge variant="destructive">Sí</Badge></td>
                <td className="px-2 py-2 text-slate-600">{i.estatus || "No hay documento vigente que lo satisfaga"}</td>
              </tr>
            )} />
        </Bloque>

        {/* 5 · Operaciones */}
        <Bloque n={5} titulo="Operaciones del cliente" icono={Landmark}
          subtitulo={`${d.operaciones.total} operación(es) registradas`}
          testid="av-bloque-operaciones">
          <Tabla testid="av-tabla-operaciones" vacio="Este cliente todavía no tiene operaciones."
            cols={["Fecha", "Fracción", "Tipo", "Rol", "Monto", "Objeto de aviso", "Estatus"]}
            rows={d.operaciones.items.slice(0, 50)}
            render={(o) => (
              <tr key={o.operacion_id} data-testid={`av-op-${o.operacion_id}`} className="border-b border-slate-100">
                <td className="whitespace-nowrap px-2 py-2">{fdate(o.fecha)}</td>
                <td className="px-2 py-2">Fr. {o.fraccion}</td>
                <td className="px-2 py-2 text-slate-600">{o.tipo || "—"}</td>
                <td className="px-2 py-2 text-slate-600">{o.rol_del_cliente || "—"}</td>
                <td className="whitespace-nowrap px-2 py-2 font-semibold">{mxn(o.monto_sin_iva)}</td>
                <td className="px-2 py-2">
                  {o.objeto_de_aviso_individual ? <Badge variant="destructive">Individual</Badge>
                    : o.objeto_de_aviso ? <Badge variant="destructive">Sí</Badge>
                      : <span className="text-slate-400">No</span>}
                </td>
                <td className="px-2 py-2 text-slate-600">{o.estatus || "—"}</td>
              </tr>
            )} />
        </Bloque>

        {/* 6 · Acumulado 6 meses */}
        <Bloque n={6} titulo="Acumulado de 6 meses contra el umbral de aviso" icono={Gauge}
          subtitulo={`Ventana ${d.acumulado_6m.ventana_inicio} a ${d.acumulado_6m.ventana_fin} · UMA ${mxn(d.acumulado_6m.uma_diario_hoy_mxn)} · cada fracción acumula contra SU propio umbral y nunca se suman entre sí`}
          testid="av-bloque-acumulado"
          extra={<Semaforo nivel={d.acumulado_6m.peor_semaforo} testid="av-acumulado-peor" />}>
          <Tabla testid="av-tabla-acumulado" vacio="La actividad no tiene fracciones activas en el catálogo."
            cols={["Fracción", "Acumulado 6m", "Umbral de aviso", "Avance", "Falta", "Ops. en ventana", "Semáforo"]}
            rows={d.acumulado_6m.por_fraccion}
            render={(f) => (
              <tr key={f.fraccion} data-testid={`av-acum-${f.fraccion}`} className="border-b border-slate-100">
                <td className="px-2 py-2 font-medium text-slate-800">Fr. {f.fraccion}</td>
                <td data-testid={`av-acum-monto-${f.fraccion}`} className="whitespace-nowrap px-2 py-2 font-semibold">{mxn(f.acumulado_6m_mxn)}</td>
                <td data-testid={`av-acum-umbral-${f.fraccion}`} className="whitespace-nowrap px-2 py-2 text-slate-600">{mxn(f.umbral_aviso_mxn)}</td>
                <td className="px-2 py-2 font-semibold">{f.porcentaje_alcanzado}%</td>
                <td className="whitespace-nowrap px-2 py-2 text-slate-600">{mxn(f.falta_para_umbral_mxn)}</td>
                <td className="px-2 py-2 text-slate-600">
                  {f.num_operaciones_en_ventana}
                  {f.reinicio_reciente && <Badge variant="destructive" className="ml-1">reinicio en ventana</Badge>}
                </td>
                <td className="px-2 py-2"><Semaforo nivel={f.semaforo} testid={`av-acum-semaforo-${f.fraccion}`} /></td>
              </tr>
            )} />
        </Bloque>

        {/* 7 · Avisos */}
        <Bloque n={7} titulo="Avisos al SAT que involucran al cliente" icono={Send}
          subtitulo={`${d.avisos.total} aviso(s) · ${d.avisos.con_acuse} con acuse presentado`}
          testid="av-bloque-avisos">
          <Tabla testid="av-tabla-avisos" vacio="Ninguna operación de este cliente se ha reportado al SAT."
            cols={["Periodo", "Fracción", "Tipo", "Estatus", "Monto del cliente", "Acuse", "Folio del acuse"]}
            rows={d.avisos.items}
            render={(a) => (
              <tr key={a.aviso_id} data-testid={`av-aviso-${a.aviso_id}`} className="border-b border-slate-100">
                <td className="whitespace-nowrap px-2 py-2 font-medium text-slate-800">{a.periodo}</td>
                <td className="px-2 py-2">Fr. {a.fraccion}</td>
                <td className="px-2 py-2 text-slate-600">{a.tipo}</td>
                <td className="px-2 py-2"><Badge variant={a.estatus === "incompleto" ? "destructive" : "secondary"}>{a.estatus}</Badge></td>
                <td className="whitespace-nowrap px-2 py-2 font-semibold">{mxn(a.monto_de_este_cliente_mxn)}</td>
                <td className="px-2 py-2">
                  {a.acuse.presentado
                    ? <Badge data-testid={`av-aviso-acuse-${a.aviso_id}`} variant="default">Acusado {fdate(a.acuse.fecha)}</Badge>
                    : <Badge variant="destructive">Sin acuse</Badge>}
                </td>
                <td className="px-2 py-2 text-xs text-slate-500">{a.acuse.folio || "—"}</td>
              </tr>
            )} />
        </Bloque>

        {/* 8 · Bitácora */}
        <Bloque n={8} titulo="Bitácora de cambios" icono={History}
          subtitulo={`${d.bitacora.total} movimiento(s) registrados`} testid="av-bloque-bitacora">
          <Tabla testid="av-tabla-bitacora" vacio="Sin movimientos en bitácora."
            cols={["Fecha", "Área", "Acción", "Usuario", "Detalle"]}
            rows={d.bitacora.items.slice(0, 50)}
            render={(b, ix) => (
              <tr key={b.id || ix} data-testid={`av-bitacora-${ix}`} className="border-b border-slate-100">
                <td className="whitespace-nowrap px-2 py-2">{fdate(b.fecha)}</td>
                <td className="px-2 py-2 text-slate-600">{b.area}</td>
                <td className="px-2 py-2 font-medium text-slate-800">{b.accion}</td>
                <td className="px-2 py-2 text-slate-600">{b.usuario}</td>
                <td className="px-2 py-2 text-xs text-slate-500">{b.descripcion}</td>
              </tr>
            )} />
        </Bloque>

        {/* 9 · Alertas de desviación */}
        <Bloque n={9} titulo="Alertas por desviación del umbral" icono={AlertTriangle}
          subtitulo={`${d.alertas_desviacion.total} alerta(s) · ${d.alertas_desviacion.rojas} en rojo · ${d.alertas_desviacion.sin_atender} sin atender`}
          testid="av-bloque-alertas">
          <Tabla testid="av-tabla-alertas" vacio="Sin alertas por desviación."
            cols={["Fecha", "Nivel", "Fracción", "Acumulado", "Umbral", "Avance", "Estatus"]}
            rows={d.alertas_desviacion.items}
            render={(a) => (
              <tr key={a.alerta_id} data-testid={`av-alerta-${a.alerta_id}`} className="border-b border-slate-100">
                <td className="whitespace-nowrap px-2 py-2">{fdate(a.fecha)}</td>
                <td className="px-2 py-2"><Semaforo nivel={a.color} testid={`av-alerta-color-${a.alerta_id}`}>{a.nivel}</Semaforo></td>
                <td className="px-2 py-2">Fr. {a.fraccion}</td>
                <td className="whitespace-nowrap px-2 py-2 font-semibold">{mxn(a.acumulado_mxn)}</td>
                <td className="whitespace-nowrap px-2 py-2 text-slate-600">{mxn(a.umbral_mxn)}</td>
                <td className="px-2 py-2">{a.porcentaje ?? "—"}%</td>
                <td className="px-2 py-2 text-slate-600">{a.estatus || "abierta"}</td>
              </tr>
            )} />
        </Bloque>

        {/* 10 · Beneficiario controlador */}
        <Bloque n={10} titulo="Beneficiario controlador" icono={UserCheck}
          subtitulo="LFPIORPI Art. 33 Bis" testid="av-bloque-bc"
          extra={<Badge data-testid="av-bc-estado"
            variant={d.beneficiario_controlador.capturado ? "default" : "destructive"}>
            {d.beneficiario_controlador.capturado
              ? `${d.beneficiario_controlador.total} capturado(s)`
              : "Falta capturar"}
          </Badge>}>
          {d.beneficiario_controlador.capturado ? (
            <Tabla testid="av-tabla-bc" vacio="—"
              cols={["Nombre", "RFC / CURP", "Tipo de control", "Porcentaje"]}
              rows={d.beneficiario_controlador.items}
              render={(b, ix) => (
                <tr key={b.id || ix} data-testid={`av-bc-${ix}`} className="border-b border-slate-100">
                  <td className="px-2 py-2 font-medium text-slate-800">{b.nombre || b.nombre_completo || "—"}</td>
                  <td className="px-2 py-2 text-slate-600">{b.rfc || b.curp || "—"}</td>
                  <td className="px-2 py-2 text-slate-600">{b.tipo_control || b.motivo || "—"}</td>
                  <td className="px-2 py-2">{b.porcentaje ?? "—"}</td>
                </tr>
              )} />
          ) : (
            <Vacio testid="av-bc-vacio">{d.beneficiario_controlador.mensaje}</Vacio>
          )}
        </Bloque>
      </div>
    </div>
  );
}
